// Die auswählbaren Werte für `Document.category`.
//
// AUSGANGSLAGE: `Document.category` ist varchar(100) Freitext — ohne
// Katalogtabelle, ohne Enum, ohne Validierung (`document.service.ts`:
// `input.category?.trim() || null`). Gesetzt wurde sie bisher AUSSCHLIESSLICH
// maschinell (Kundenakten-Importer, Mail-Anhang-Import, Register-Abdruck);
// PROD kennt am 15.08.2026 genau sechs Werte: „Alte Kundenakte" (110.999),
// „Schaden" (17.374), „E-Mail-Anhang" (79), leer (17), „Handelsregister" (3),
// „Objektfoto" (1). Im Frontend kannten `documentService.upload()` und
// `.update()` den Parameter zwar — es gab schlicht keinen Aufrufer.
//
// Diese Liste ist deshalb kein neuer Katalog, sondern die Bestandsaufnahme plus
// EIN neuer Wert. Sie bleibt eine Frontend-Konstante: eine Katalogtabelle wäre
// ohne Not eine zweite Wahrheit neben 128.473 bereits geschriebenen Freitexten.
//
// Maschinell gesetzte Kategorien stehen NICHT zur Auswahl: „Alte Kundenakte"
// markiert die Legacy-Kundenakten-Ordnerstruktur und steuert den Explorer-
// Scope (`listFolders`), „Schaden" und „E-Mail-Anhang" entstehen aus dem
// jeweiligen Import, und „E-Mail-Archiv" (Cluster ABLAGE, seit 23.08.2026)
// kennzeichnet die dauerhaft abgelegte `.eml` einer archivierten Mail — an ihr
// hängt der eigene Zugriffsschutz (`MailArchiveAccessService`) und die Sperre
// gegen das harte Löschen. Sie von Hand zu vergeben würde die Aussage dieser
// Werte entwerten.

/**
 * Satzung des Vereins (§ 25 BGB), der AG (§ 23 AktG) und der Stiftung (§ 81
 * BGB) sowie Gesellschaftsvertrag der GmbH (§ 2 GmbHG) und der OHG/KG
 * (§§ 105, 161 HGB) — EINE Kategorie, weil es fachlich dieselbe Sache ist: die
 * Verfassungsurkunde des Rechtsträgers. § 2 GmbHG nennt den
 * Gesellschaftsvertrag der Ein-Personen-GmbH selbst „Satzung". Zwei Kategorien
 * würden die Frage „wo liegt die Verfassung dieses Rechtsträgers" in zwei
 * Abfragen zerlegen und den Sachbearbeiter bei der GmbH zum Raten zwingen.
 *
 * Der Schrägstrich ist ungefährlich: als Ordnertrenner liest der Explorer nur
 * den `fileName`; `category` wird nirgends gesplittet, sondern exakt verglichen
 * (`LOWER(category) = :category`) und roh angezeigt.
 */
export const KATEGORIE_SATZUNG = "Satzung / Gesellschaftsvertrag";

/** Registerabdruck aus dem Handelsregister — vom Abdruck-Service gesetzt. */
export const KATEGORIE_HANDELSREGISTER = "Handelsregister";

/** Foto eines Risikoobjekts (Gebäude, Anlage, Fuhrpark). */
export const KATEGORIE_OBJEKTFOTO = "Objektfoto";

/**
 * Unterschriebene Erstinformation bzw. Vollmacht (PO 22.09.2026).
 *
 * Neu vergeben, nicht vorgefunden: Bis dahin lag der unterschriebene Scan als
 * Datei unter `/media/authorities/…` und hatte gar keine Zeile in `Document` —
 * er war weder durchsuchbar noch in der Kundenakte zu finden. Neue Scans sind
 * gewöhnliche Dokumente und tragen diese Kategorie; der Chip in den
 * Kontaktdetails hängt nur noch als Verweis daran.
 */
export const KATEGORIE_VOLLMACHT = "Vollmacht";

export type DokumentKategorie = {
  value: string;
  /** Kurzer Zusatz im Dropdown — was gehört hier hinein, was nicht. */
  hinweis?: string;
};

/**
 * Auswahl beim Hochladen und beim nachträglichen Zuordnen, in der Reihenfolge
 * der erwarteten Häufigkeit.
 */
export const DOKUMENT_KATEGORIEN: readonly DokumentKategorie[] = [
  { value: KATEGORIE_SATZUNG, hinweis: "Satzung, Gesellschaftsvertrag, Stiftungssatzung" },
  { value: KATEGORIE_HANDELSREGISTER, hinweis: "Register-Abdruck (HR, VR, GnR, PR)" },
  { value: KATEGORIE_OBJEKTFOTO, hinweis: "Foto eines Risikoobjekts" },
  { value: KATEGORIE_VOLLMACHT, hinweis: "Unterschriebene Erstinformation / Vollmacht" },
];

/** varchar(100) — der Grenzwert gehört an die Konstante, nicht in einen Kommentar. */
export const KATEGORIE_MAX_LAENGE = 100;
